/**
 * AdminUpdateVideosModal.tsx
 *
 * Modal component to display all videos with pending approval (status = 'pending').
 * Shows searchable list with Video ID, User ID, Name, Mobile, Upload Date, and View button.
 * Matches project theme with gradient backgrounds and pagination (4 rows per page).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface VideoItem {
  videoId: number;
  userId: string;
  firstName: string;
  lastName: string;
  mobile: string;
  videoUrl: string;
  uploadedAt: string;
  status: string;
}

interface AdminUpdateVideosModalProps {
  open: boolean;
  onClose: () => void;
}

import { API_CONFIG } from '../../config/api.config';

const API_URL = API_CONFIG.getBackendOrigin();

export default function AdminUpdateVideosModal({ open, onClose }: AdminUpdateVideosModalProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 4;

  useEffect(() => {
    if (open) {
      fetchItems();
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [open]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/update-videos`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error('Failed to fetch video updates:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        item.userId.toLowerCase().includes(term) ||
        item.firstName.toLowerCase().includes(term) ||
        item.lastName.toLowerCase().includes(term) ||
        item.mobile.includes(term) ||
        item.videoId.toString().includes(term)
    );
  }, [items, searchTerm]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const handleView = (userId: string) => {
    onClose();
    setTimeout(() => {
      navigate(`/profile/${userId}`);
    }, 100);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Update videos modal"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8"
    >
      {/* Full-screen themed backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(2,6,23,0.75), rgba(7,16,26,0.75)), var(--dashboard-bg, linear-gradient(135deg,#8694a1 0%,#ecfeff 100%))',
          backdropFilter: 'blur(6px)',
        }}
      />

      {/* Modal container */}
      <div className="relative w-full max-w-6xl rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10">
        {/* Title bar */}
        <div
          className="px-6 py-5 flex items-center justify-between gap-4"
          style={{
            backgroundImage: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            color: 'white',
          }}
        >
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold leading-tight">Update Videos</h3>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full bg-white/12 text-sm font-medium"
              title={`${items.length} videos`}
            >
              {items.length}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="text-sm text-white/90 px-3 py-1 rounded bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="text-sm text-white/90 px-3 py-1 rounded bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white p-6">
          {/* Search Filter */}
          <div className="mb-5">
            <label htmlFor="search-term" className="block text-sm font-medium text-slate-700 mb-1">
              Search
            </label>
            <input
              id="search-term"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by Video ID, User ID, Name, or Mobile..."
              className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-200"
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr
                  className="text-left"
                  style={{
                    backgroundImage: 'var(--card-gradient-total, linear-gradient(90deg,#7C3AED 0%,#EC4899 100%))',
                    color: 'white',
                  }}
                >
                  <th className="py-3 pr-4 pl-4 w-24">Video ID</th>
                  <th className="py-3 pr-4 w-36">User ID</th>
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Mobile</th>
                  <th className="py-3 pr-4 w-40">Uploaded At</th>
                  <th className="py-3 pr-4 w-28">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-500">
                      {searchTerm ? 'No videos found matching your search.' : 'No pending videos found.'}
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item) => (
                    <tr key={item.videoId} className="border-t last:border-b-0">
                      <td className="py-3 pr-4 pl-4 font-mono align-middle">{item.videoId}</td>
                      <td className="py-3 pr-4 font-mono align-middle">{item.userId}</td>
                      <td className="py-3 pr-4 align-middle">{item.firstName} {item.lastName}</td>
                      <td className="py-3 pr-4 align-middle">{item.mobile}</td>
                      <td className="py-3 pr-4 align-middle text-xs">{formatDate(item.uploadedAt)}</td>
                      <td className="py-3 pr-4 align-middle">
                        <button
                          onClick={() => handleView(item.userId)}
                          className="px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer summary and pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Showing {startIndex + 1}–{Math.min(endIndex, filteredItems.length)} of {filteredItems.length} videos.
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <span className="text-sm font-medium text-slate-700">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
